import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../shared/api/client";
import type {
  EnvironmentDetail,
  ReleaseApprovalRole,
  ReleaseGate,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, LoadingState, Select } from "../../shared/ui";
import { APPROVAL_ROLES, APPROVAL_ROLE_LABELS } from "../releases/releaseVocabulary";

/**
 * The gates, in the order the readiness panel shows them, with what each asks.
 *
 * <p>Written out rather than derived, because an administrator setting a policy needs to know
 * what they are switching on. "APPROVALS" tells somebody nothing about whether a sandbox should
 * have it.
 */
const GATES: [ReleaseGate, string, string][] = [
  ["SCOPE", "Scope", "There is work in the release, and it has been frozen."],
  ["DEVELOPMENT", "Development", "Every mandatory item has finished development."],
  ["QA", "QA", "Everything that needed testing passed, or was waived."],
  ["CRITICAL_BUGS", "Critical bugs", "No unaccepted blocker or critical known issue."],
  ["PULL_REQUESTS", "Pull requests", "Required merges are done. Recorded by hand."],
  ["BUILD", "Build", "The chosen build succeeded and an artifact exists."],
  ["PREREQUISITES", "Prerequisites", "Every mandatory preparation step is complete."],
  ["ROLLBACK_PLAN", "Rollback plan", "A plan exists for putting things back."],
  ["APPROVALS", "Approvals", "Everyone who has to say yes has said it."],
  ["DOCUMENTS", "Documents", "Every required document actually exists."],
  ["CUSTOM_FIELDS", "Required fields", "Your organisation's own questions are answered."],
  ["DEPLOYMENT_WINDOW", "Deployment window", "There is a date, and now is inside it."],
  ["EXECUTION", "Execution", "Every planned runbook step ran. Checked at completion."],
  [
    "POST_DEPLOYMENT_VALIDATION",
    "Post-deployment validation",
    "The checks passed and the observation window elapsed. Checked at completion.",
  ],
];

/**
 * What a release must satisfy before it may go to this environment (§63).
 *
 * <p>This is the difference between a sandbox and production. Judging both by the same list means
 * either the sandbox is unusable — five approvals to deploy a branch — or production is
 * unguarded, and teams reliably resolve that by doing sandbox deployments somewhere the record
 * cannot see them.
 *
 * <p>Ticking nothing is a legitimate answer and is not the same as never having decided. An
 * environment nobody has configured asks for every gate, because forgetting to set a policy must
 * not be the way to create the one place a release can reach unchecked.
 */
export function EnvironmentGatesDrawer({
  environment,
  open,
  onClose,
  onSaved,
}: {
  environment: EnvironmentDetail | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<ReleaseGate>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [comesAfter, setComesAfter] = useState<string>("");
  const [roles, setRoles] = useState<Set<ReleaseApprovalRole>>(new Set());

  const environments = useQuery({
    queryKey: ["environments", "all"],
    queryFn: () => api.get<EnvironmentDetail[]>("/api/v1/environments/all"),
    enabled: open,
  });

  const current = useQuery({
    queryKey: ["environment-gates", environment?.id],
    queryFn: () =>
      api.get<ReleaseGate[]>(`/api/v1/environments/${environment?.id}/gates`),
    enabled: open && environment != null,
  });

  /* Who must sign off before a release may come here (§31). Separate from the gates because it
     is a different question: the gates say what must be true, this says who must say so. */
  const currentRoles = useQuery({
    queryKey: ["environment-approvals", environment?.id],
    queryFn: () =>
      api.get<ReleaseApprovalRole[]>(`/api/v1/environments/${environment?.id}/approvals`),
    enabled: open && environment != null,
  });

  useEffect(() => {
    if (current.data) {
      setSelected(new Set(current.data));
    }
  }, [current.data]);

  useEffect(() => {
    if (currentRoles.data) {
      setRoles(new Set(currentRoles.data));
    }
  }, [currentRoles.data]);

  useEffect(() => {
    if (open) {
      setComesAfter(environment?.requiresEnvironmentId ?? "");
      setError(null);
    }
  }, [open, environment]);

  const toggle = (gate: ReleaseGate) =>
    setSelected((now) => {
      const next = new Set(now);
      if (next.has(gate)) {
        next.delete(gate);
      } else {
        next.add(gate);
      }
      return next;
    });

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/environments/${environment?.id}/gates`, [...selected]);
      await api.put(`/api/v1/environments/${environment?.id}/approvals`, [...roles]);
      await api.put(`/api/v1/environments/${environment?.id}/comes-after`, {
        requiresEnvironmentId: comesAfter === "" ? null : comesAfter,
      });
      await queryClient.invalidateQueries({ queryKey: ["environments"] });
      await queryClient.invalidateQueries({ queryKey: ["environment-gates"] });
      await queryClient.invalidateQueries({ queryKey: ["release-readiness-for"] });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that policy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="What must be true to deploy here?"
      subtitle={environment?.name}
      width="lg"
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving ? "Saving…" : "Save policy"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {environment?.production && (
          <InlineAlert tone="warning">
            This is a production environment. Whatever you leave unticked here is a check that
            will not be made before a release reaches customers.
          </InlineAlert>
        )}

        {selected.size === 0 && (
          <InlineAlert tone="info">
            Nothing ticked: any release with work in it may be deployed here. That is the right
            answer for a sandbox — deploying half-finished, untested work is what one is for.
          </InlineAlert>
        )}

        {/*
          Order, as distinct from substance. The checkboxes below say what a release must satisfy;
          this says where it must already have been. Without it a release could clear every check
          on paper and reach production having never actually run anywhere.
        */}
        <Field
          label="Comes after"
          hint="A release may only be deployed here once it has succeeded there. Chain them and 'no production without development' follows without anybody writing that rule."
        >
          <Select value={comesAfter} onChange={(event) => setComesAfter(event.target.value)}>
            <option value="">Anywhere — no order enforced</option>
            {(environments.data ?? [])
              .filter((row) => row.id !== environment?.id)
              .map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
          </Select>
        </Field>

        {current.isPending ? (
          <LoadingState label="Loading the policy…" />
        ) : (
          <div className="divide-y divide-ink-100 rounded-lg border border-ink-200">
            {GATES.map(([gate, name, asks]) => (
              <label
                key={gate}
                className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-ink-50"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink-300"
                  checked={selected.has(gate)}
                  onChange={() => toggle(gate)}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ink-900">{name}</span>
                  <span className="block text-xs text-ink-600">{asks}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        {/* Who signs off, as distinct from what must be true. A release reaching production
            after four sign-offs in development often needs only the project manager again —
            the rest already said yes on the way through. */}
        <Field
          label="Who must sign off"
          hint="Leave all unticked and this destination asks for no sign-off at all — right when everybody already approved earlier in the journey."
        >
          <div className="divide-y divide-ink-100 rounded-lg border border-ink-200">
            {APPROVAL_ROLES.map((role) => (
              <label
                key={role}
                className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-ink-50"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 rounded border-ink-300"
                  checked={roles.has(role)}
                  onChange={() =>
                    setRoles((now) => {
                      const next = new Set(now);
                      if (next.has(role)) {
                        next.delete(role);
                      } else {
                        next.add(role);
                      }
                      return next;
                    })
                  }
                />
                <span className="text-sm text-ink-900">{APPROVAL_ROLE_LABELS[role]}</span>
              </label>
            ))}
          </div>
        </Field>

        <p className="text-xs text-ink-500">
          Applies to future checks only. A release already deployed here was judged by the policy
          in force at the time, and that verdict stays on its record.
        </p>
      </div>
    </Drawer>
  );
}
